import { Switch, Divider, Typography, Radio, Select } from 'antd'
import { BASE_PADDING, SEVERITY_COLOR, SEVERITY_COLOR_TEXT } from 'constants/Layout'
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import _, { filter, isEmpty, isNull } from 'lodash';
import moment from 'moment'
import { Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Label, Area, ComposedChart, DefaultTooltipContent } from 'recharts'
import { withRouter } from 'react-router-dom'
import { WidgetEmpty } from './WidgetEmpty'
import { getFilterProps, Sorters, numberFormatter } from "utils/tableUtils";
import { updateURLSearch, parseURLSearch } from "utils/urlUtil";
import { useGlobalFilterContext } from 'contexts/GlobalFilterContext'
import FindingAPI from 'restAPI/FindingAPI'
import { BASE_SEVERITIES, BASE_SEVERITY_OBJECT, SEVERITY_NAMES } from 'views/Findings/Constants'
import FilterBarWithFilterButton from 'components/FilterBarWithFilterButton'
import { populateNonExistentDate } from 'utils/dateUtils';
import { ToolCategory } from 'views/Integrations/ToolCategory';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import WidgetCard from './WidgetCard';
import useUrlFilters from 'hooks/useUrlFilters';
import useUnResolvedFindingStatus from 'hooks/useUnResolvedFindingStatus';
import useResolvedFindingStatus from 'hooks/useResolvedFindingStatus';
import { formatNumber } from 'utils/numberUtil';

const NO_DATA_DESCRIPTION = "“Findings are the portals of discovery”. We will be displaying your new findings in a moment.";
const NO_DATA_AVAILABLE = "No Data Available";
export const LINE_CHART = "Line", AREA_CHART = "Area";
export const DURATION_OPTIONS = [{ label: "All time", value: 0 }, { label: "7 days", value: 7 }, { label: "30 days", value: 30 }, { label: "60 days", value: 60 }, { label: "90 days", value: 90 }]

/**
 * 
 * @param {onChange} - should return all the filters/config that needs to be saved on custom dashboard
 * @param {config} - contains the initial config that will fetched from the custom dashboard level widget configuration so that initial filters can be set
 * @returns 
 */
const VulnerabilityTrend = React.memo(function VulnerabilityTrend({ environment , allBUAggregated, config, onChange, widgetKey, businessUnits, hideViewMore, ...props}) {
    const mountRef = useRef(false);
    // useOtherFiltersFromUrl is true then hide the local filters and use scanType and source from url and show all severities
    const { useOtherFiltersFromUrl } = props;

    const { productId, subproductId } = props.match.params;
    const [chartType, setChartType] = useState(config?.chartType ?? AREA_CHART);
    const [selectedDuration, setSelectedDuration] = useState(config?.selectedDuration ?? 0);
    const [logScale, setLogScale] = useState(config?.logScale ?? false);
    const [data, setData] = useState(null);
    const [activeFilters, setActiveFilters] = useState(() => {
        if(config?.filters){
            return config.filters;
        }
        return {
            severity: useOtherFiltersFromUrl ? [] : [SEVERITY_NAMES.critical, SEVERITY_NAMES.high]
        }
    });
    const [availableFilters, setAvailableFilters] = useState({});
    const { globalFilterHash } = useGlobalFilterContext();

    const productsFromUrl = useUrlFilters("product");
    const subProductsFromUrl = useUrlFilters("subProduct");
    const envFromUrl = useUrlFilters("environmentName");
    const tagsFilter = useUrlFilters("keyValue");

    const scanTypeFromUrl = useUrlFilters("scanType");
    const sourceFromUrl = useUrlFilters("source");
    const productStatusFromUrl = useUrlFilters("productStatus");
    const subProductStatusFromUrl = useUrlFilters("subProductStatuses");
    const unResolvedStatus = useUnResolvedFindingStatus();
    const resolvedStatus = useResolvedFindingStatus();

    const scanType = useOtherFiltersFromUrl ? scanTypeFromUrl : activeFilters.scanType;
    const appliedToolFilters = useOtherFiltersFromUrl ? sourceFromUrl : activeFilters.source;
    const appliedSeverities = useMemo(() => {
        return (isEmpty(activeFilters.severity) ? BASE_SEVERITIES : activeFilters.severity).slice().sort(Sorters.SEVERITY());
    }, [activeFilters.severity]);

    // saving is handled in custom dashboard
    useEffect(() => {
        if(!mountRef.current){
            mountRef.current = true;
            return;
        }
        if(_.isFunction(onChange)){
            onChange(widgetKey, {
                filters: activeFilters,
                selectedDuration, chartType, logScale
            })
        }
    }, [widgetKey, activeFilters, selectedDuration, chartType, logScale, onChange]);

    useEffect(() => {
        setData(null);
        let aggregateBy;
        const fetchData = () => {
            const startDate = selectedDuration ? moment().subtract(selectedDuration, "d").utc().format() : false, endDate = moment().utc().format();
            const {promise, cancel} = FindingAPI.getTrends({
                filters: _.omitBy({
                    product: productId ? [productId] : productsFromUrl, 
                    subProduct: subproductId ? [subproductId] : subProductsFromUrl, 
                    environmentName: environment ? [environment] : envFromUrl,
                    source: appliedToolFilters || undefined,
                    scanType: scanType,
                    status: unResolvedStatus,
                    keyValue: tagsFilter,
                    productStatus: productStatusFromUrl,
                    subProductStatuses: subProductStatusFromUrl,
                }, _.isEmpty),
                startDate, 
                endDate, 
                aggregateBy,
                businessUnits
            }, allBUAggregated);
            promise.then(data => {
                if (!isEmpty(data.trend)) {
                    setData(formatData(data, { startDate, endDate, unResolvedStatus, resolvedStatus }));
                } else {
                    setData([]);
                }
            }).catch(err => {
                setData([]);
                console.error(err)
            })
            
            return cancel;
        }

        return fetchData()
    }, [
        productId, subproductId, globalFilterHash, selectedDuration, appliedToolFilters, 
        allBUAggregated, scanType, environment, envFromUrl, productsFromUrl, subProductsFromUrl,
        businessUnits, tagsFilter,productStatusFromUrl, subProductStatusFromUrl, unResolvedStatus,
        resolvedStatus,
    ])

    useEffect(() => {
        const filters = _.omitBy({
            product: productId ? [productId] : productsFromUrl, 
            subProduct: subproductId ? [subproductId] : subProductsFromUrl, 
            environmentName: environment ? [environment] : envFromUrl,
            scanType: scanType,
            status: unResolvedStatus
        }, _.isEmpty);
        const { cancel, promise } = FindingAPI.getFilterValues({
            filters,
            fields: ["source"]
        },
        allBUAggregated
        )
        promise.then(setAvailableFilters)
        return cancel;
    }, [environment, productId, subproductId, globalFilterHash, allBUAggregated, scanType, envFromUrl, productsFromUrl, subProductsFromUrl, unResolvedStatus])

    const redirectToFindings = useCallback(() => {
        const { filters } = parseURLSearch(props.location.search);
        const urlFilters = _.omit(filters, ["selectedDuration", "foundOn"]);
        if(selectedDuration){
            const startDate = moment().subtract(selectedDuration, "d").utc().format();
            const endDate = moment().utc().format();
            urlFilters.foundOn = [startDate, endDate];
        }
        updateURLSearch(props.history, {
            filters: {
                source: appliedToolFilters,
                product: productId,
                subProduct: subproductId, 
                ...urlFilters
            } 
        }, "/dashboard/findings" )
    },[props.history, props.location.search, productId, subproductId, appliedToolFilters,selectedDuration])

    const filterColumns = useMemo(() => [{
        title: <Vocabulary name="severity" />,
        dataIndex: "severity",
        filterIndex: 1,
        ...getFilterProps({ filters: BASE_SEVERITIES, noFilterFunction: true, sortOptions: false, })
    }, {
        title: "Tool",
        dataIndex: "source",
        filterIndex: 1,
        ...getFilterProps({ filters: availableFilters.source, noFilterFunction: true })
    }, {
        title: 'Scan Type',
        dataIndex: "scanType",
        filterIndex: 1,
        ...getFilterProps({ filters: Object.values(ToolCategory), noFilterFunction: true })
    }], [availableFilters.source]);

    const scale = logScale ? "log" : undefined;

    const widgetHeight = "97%";

    const onChartTypeChange = useCallback((e) => {
        setChartType(e.target.value);
    }, []);

    // if(isNull(data)){
    //     return <WidgetCard.Spinner height={widgetHeight} spinning={isNull(data)} />
    // }

    return (
        <>
            <WidgetCard.Extras>
                <div className="d-flex align-items-center" style={{ fontSize: 13 }}>
                    {/* {!isEmpty(data) &&  */}
                        <>
                            <Select size='small' style={{ width: 85 }} options={DURATION_OPTIONS} value={selectedDuration} onChange={setSelectedDuration} />
                            <Divider type="vertical"/>
                            <Radio.Group options={[LINE_CHART, AREA_CHART]} value={chartType} onChange={onChartTypeChange} optionType='button' buttonStyle="solid" size='small' />
                            <Divider type="vertical"/>
                            Log Scale <Switch size="small" className="m-l-xs" checked={logScale} onChange={setLogScale} />
                            {
                                useOtherFiltersFromUrl
                                ?
                                null
                                :
                                <>
                                    <Divider type="vertical"/>
                                    <FilterBarWithFilterButton text="" filterFields={filterColumns} prefixText=""  handleFilterChange={setActiveFilters} filters={activeFilters} />
                                </>
                            }
                        </>
                    {/* } */}
                    {!allBUAggregated && !hideViewMore && <><Divider type="vertical"/><Typography.Link onClick={redirectToFindings}>View more...</Typography.Link></>}
                </div>
            </WidgetCard.Extras>
            <VulnerabilityTrendChart widgetHeight={widgetHeight} data={data} scale={scale} appliedSeverities={appliedSeverities} chartType={chartType} />
        </>
    )
})

export function VulnerabilityTrendChart({ widgetHeight, data, scale, appliedSeverities, chartType }) {
    return (
        <WidgetCard.Spinner height={widgetHeight} spinning={isNull(data)}>
            <ResponsiveContainer width="100%" height={widgetHeight} className="overflow-hidden">
                {
                    !data || !data?.length ? 
                    <WidgetEmpty imgPath="/images/vulnerability_trend.png" description={!data ? NO_DATA_DESCRIPTION : NO_DATA_AVAILABLE} />
                    :
                    <ComposedChart
                        key={scale}
                        data={data}
                        margin={{ top: BASE_PADDING, left: 0,bottom:5, right: 20 }}
                        // className='overflow-hidden'
                    >
                        <Tooltip content={<VulnerabilityTrendCustomTooltip doubleGraph={false} isSeverityOrderChange={true} formatter={(value, name, props) => [formatNumber(value, 'long'), name !== "Total" ? getVocabulary(name.toLowerCase()) : name]} />} />
                        <XAxis padding={{right: 10}} dataKey="date" scale={"point"}>
                        <Label className="text-bold" value="Date" offset={-3} position="insideBottom" />
                        </XAxis>
                        <YAxis padding={{ top: 10 }} scale={scale} domain={[scale ? 0.9 : 0, "dataMax + 1"]} allowDecimals={false} allowDataOverflow tickFormatter={numberFormatter}>
                        <Label  style={{ textAnchor: 'middle' }} className="text-bold" value="Findings" angle={-90} position="insideLeft" />
                        </YAxis>
                        {
                            chartType === LINE_CHART
                            ?
                            appliedSeverities.map(renderGraph(Line))
                            :
                            appliedSeverities.map(renderGraph(Area, true, scale))
                        }
                        {
                        }
                    </ComposedChart>
                }
            </ResponsiveContainer>
        </WidgetCard.Spinner>
    )
}

export default withRouter(VulnerabilityTrend)

export const formatData = ({ aggregateBy, trend = [] }, { startDate, selectedDuration, aggregateByMonth, endDate, unResolvedStatus, resolvedStatus, formatDateWithYear = false }) => {
    // aggregateByMonth in the function param is depricated.
    if (aggregateBy) {
        aggregateByMonth = aggregateBy === 'Month';
    }
    const list = [];
    trend.sort(Sorters.DATE("date")).forEach((currentObject, i) => {
        const mitigatedStats = {};
        let totalMitigated = 0, totalNonMitigated = 0, total = 0;
        let newFindings = 0, newMitigated = 0;
        const prevDayData = list[i - 1];

        const allMitigatedStats = filter(currentObject.stats, (value, index) => resolvedStatus.includes(index));
        allMitigatedStats.forEach((currentValue) => {
            BASE_SEVERITIES.forEach((severity) => {
                const value = currentValue.severity[severity] || 0;
                mitigatedStats[`mitigated_${i}`] = value;
                totalMitigated += value;
            });
        });


        // Summing up open, false +ve, accepted risk and confirmed findings stats
        const allNonMitigatedStats = filter(currentObject.stats, (value, index) => unResolvedStatus.includes(index));
        const combinedNonMitigatedStats = allNonMitigatedStats.reduce((accumulator, currentValue) => {
            BASE_SEVERITIES.forEach((severity) => {
                const value = currentValue.severity[severity] || 0;
                accumulator[severity] += value;
                totalNonMitigated += value;
            })
            return accumulator;
        }, { ...BASE_SEVERITY_OBJECT });

        total = totalMitigated + totalNonMitigated;
        const currentDateMoment = moment(currentObject.date);

        if(prevDayData){
            newFindings = Math.max(0, total - prevDayData.total);
            newMitigated = Math.max(0, totalMitigated - prevDayData.totalMitigated);
        } else {
            // If there is no previous day data, then current data is considered as new data
            // For first day, total and new will be the same
            newFindings = total;
            newMitigated = totalMitigated;
        }

        const formattedDate = formatDateWithYear ? "DD MMM'YY" : "DD MMM";

        list.push({
            date: currentDateMoment.format(aggregateByMonth ? "MMM YYYY" :  formattedDate),
            dateRaw: currentObject.date,
            ...combinedNonMitigatedStats,
            ...mitigatedStats,
            totalMitigated, 
            openFindings: totalNonMitigated,
            total,
            newFindings,
            newMitigated
        });
    });

    populateNonExistentDate(startDate, list, selectedDuration, aggregateBy, endDate);
    
    if(startDate){
        // [startDate] = getStartAndEndDateForQuickOption({ label: selectedDuration });
        const startDateMoment = moment(startDate);
        console.log(startDateMoment.toDate())
        for(let i = 0; i < list.length; i++){
            const object = list[i];
            // console.log(startDateMoment.diff(object.dateRaw, "day"), new Date(object.dateRaw))
            if(startDateMoment.isAfter(object.dateRaw)){
                list.splice(i, 1);
                i--;
            }
        }
    }

    return list;
}

function renderGraph(Component, stacked = false, scale, dataPrefix = ""){
    const isAreaChart = Component === Area;
    return (severity, i) => {
        return (
            <Component
                key={i} 
                connectNulls={isAreaChart && scale !== 'log'} // for visual backward compatibility with old Recharts
                type="monotone" 
                dataKey={dataPrefix + severity} 
                strokeWidth={2}
                name={severity}
                stroke={SEVERITY_COLOR_TEXT[severity]} 
                fill={SEVERITY_COLOR[severity]} 
                stackId={stacked ? "a" : undefined}
                dot={false}
            />
        )
    }
}

export function VulnerabilityTrendCustomTooltip(props){
    const data = props.isSeverityOrderChange ? props.payload.reverse() : props.payload;
    const payload = props.doubleGraph ? data.slice(0, data.length / 2) : data;
    if (!_.isEmpty(payload)) {
        const selectedFilters = _.map(payload, 'dataKey');
        const totalPayload = payload[0].payload;
        let value = 0;
        selectedFilters.forEach((severity) => {
            value += totalPayload[severity];
        });
        
        payload.push({ name: 'Total', value: formatNumber(+value.toFixed(0), 'long') });
    }
    return <DefaultTooltipContent {...props} payload={payload} />
}